import isString from 'lodash-es/isString';
import { isEmpty, validDay, validMonth } from './utils/helpers';
/**
 * Run the rule function on a value for a specific field
 */
export function checkRule(rule, value) {
    const [ruleFunction, message] = rule;
    return !ruleFunction(value) ? message : null;
}
/**
 * Check if the value passed is empty
 */
export function required(value) {
    return !isEmpty(value);
}
/**
 * Check if the string value is less than or equal to the length specified
 * @example
 * ```ts
 * import { Validation } from "@catawiki/runtime-toolkit";
 *
 * const validator = Validation.createValidator({
 *   username: [
 *     [Validation.validMaxLength(11), 'Max length 11 characters']
 *   ],
 * });
 *
 * const result = validator({ username: 'myusername' });
 * ```
 */
export function validMaxLength(length) {
    return (value) => isString(value) && value.length <= length;
}
/**
 * Check if the string value is less than or equal to the length specified
 * if the value is not empty
 * @example
 * ```ts
 * import { Validation } from "@catawiki/runtime-toolkit";
 *
 * const validator = Validation.createValidator({
 *   username: [
 *     [Validation.validMaxNonEmptyLength(11), 'Max length 11 characters']
 *   ],
 * });
 *
 * const result = validator({ username: 'myusername' });
 * ```
 */
export function validMaxNonEmptyLength(length) {
    return (value) => isEmpty(value) || (isString(value) && value.length <= length);
}
/**
 * Validate a date against the format: YYYY-MM-DD
 * @param {unknown} date e.g. '1986-11-09'
 */
export function validDate(date) {
    if (isEmpty(date) || Boolean(date) === false || !isString(date)) {
        return false;
    }
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const result = date.match(dateRegex);
    if (!result) {
        return false;
    }
    const [, year, month, day] = result;
    return !(!validDay(+year, +month, +day) || !validMonth(+month));
}
/**
 * Validate an email
 *
 * Based on the most common stackoverflow subject regarding email validation:
 * https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
 * this should cover most cases.
 *
 * @param {unknown} email e.g. 'name.surname@mail.com'
 */
export function validEmail(email) {
    if (!isString(email)) {
        return false;
    }
    const rx = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return rx.test(email.toLowerCase());
}
/**
 * A simple validation functionality for validating a field/value Object
 *
 * @example
 *
 * ```ts
 * import { Validation } from "@catawiki/runtime-toolkit";
 *
 * const validator = Validation.createValidator({
 *   firstName: [
 *     [ Validation.required, 'This field is required' ]
 *     [ Validation.validLength(24), 'Max length 24 characters' ]
 *   ],
 *   dateOfBirth: [
 *     [ Validation.required, 'This field is required' ],
 *     [ Validation.validDate, 'Please select a valid date' ],
 *   ]
 * });
 *
 * const result = validator({
 *   firstName: '',
 *   dateOfBirth: '1986-09-11',
 * });
 *
 * // If there are errors it returns an Object with fieldName/Array of errorMessages pairs:
 *
 * // {
 * //  firstName: ["This field is required"]
 * // }
 *
 * // If there are no errors it returns null.
 * ```
 */
export function createValidator(rules) {
    return (data = {}) => {
        const allErrors = Object.keys(data).reduce((obj, field) => {
            const fieldErrors = rules[field]
                ?.map((rule) => checkRule(rule, data[field]))
                .filter((ruleError) => !!ruleError && ruleError.length > 0);
            return fieldErrors?.length ? { ...obj, [field]: fieldErrors } : obj;
        }, {});
        return Object.keys(allErrors).length ? allErrors : {};
    };
}
